import Button from 'lib/components/button/Button';
import ErrorBag from 'lib/components/error-bag/ErrorBag';
import Input from 'lib/components/input/Input';
import Modal from 'lib/components/modal/Modal';
import Select from 'lib/components/select/Select';
import Textarea from 'lib/components/textarea/Textarea';
import { STAGE_KIND, STRICTNESS_OPTIONS, strictnessOf } from 'lib/dynamicModificationStages';
import { periodUnitOptions, RULE_PRESETS, STAGE_KIND_OPTIONS } from '../../dynamicRuleStages';

import './SettingsDynamicRuleModal.scss';

const NO_STAGE = { value: null, label: 'Stay in this stage' };

const digitsOnly = (value) => value.replace(/[^0-9]/g, '');

function StageSelect({ value, options, onChange, errors, allowNone, testId }) {
  const selectOptions = allowNone ? [NO_STAGE, ...options] : options;
  return (
    <div className='stage-field'>
      <Select
        isInModal
        className='stage-select'
      options={selectOptions}
        value={
          selectOptions.find((option) => option.value === Number(value)) ||
          (allowNone ? NO_STAGE : null)
        }
        onChange={(option) => onChange(option?.value ?? null)}
        errors={errors}
        placeholder='Pick a stage'
        data-testid={testId}
      />
    </div>
  );
}

function NumberInput({ value, onChange, errors, testId, ...props }) {
  return (
    <Input
      className='number-input'
      inputMode='numeric'
      value={value}
      onChange={(ev) => onChange(digitsOnly(ev.target.value))}
      errors={errors}
      data-testid={testId}
      {...props}
    />
  );
}

function PeriodUnitSelect({ value, amount, onChange, errors, testId }) {
  const options = periodUnitOptions(amount);
  return (
    <div className='stage-field'>
      <Select
        isInModal
        className='period-unit-select'
        options={options}
        value={options.find((option) => option.value === value) || options[0]}
        onChange={(option) => onChange(option.value)}
        errors={errors}
        data-testid={testId}
      />
    </div>
  );
}

function StageRow({ stage, index, stageOptions, errors, onChange, onRemove, canRemove }) {
  const change = (changes) => onChange(index, changes);
  const testId = (name) => `SettingsDynamicRuleModal.stage${index}.${name}`;
  const fieldErrors = (field) => errors[`stages.${index}.${field}`];
  const bind = (field) => ({
    value: stage[field],
    onChange: (value) => change({ [field]: value }),
    errors: fieldErrors(field),
    testId: testId(field),
  });
  const isSkip = stage.kind === STAGE_KIND.SKIP;
  const otherStages = stageOptions.filter((option) => option.value !== Number(stage.stageNo));

  return (
    <div className={`dynamic-rule-stage ${isSkip ? 'is-skip' : ''}`}>
      <div className='stage-head'>
        <NumberInput label='No.' {...bind('stageNo')} />
        <Input
          label='Stage name'
          containerClassName='stage-name'
          value={stage.name}
          onChange={(ev) => change({ name: ev.target.value })}
          errors={fieldErrors('name')}
          data-testid={testId('name')}
        />
        <div className='stage-field'>
          <Select
            isInModal
            label='What happens'
            className='stage-kind'
            options={STAGE_KIND_OPTIONS}
            value={STAGE_KIND_OPTIONS.find((option) => option.value === stage.kind)}
            onChange={(option) => change({ kind: option.value })}
            data-testid={testId('kind')}
          />
        </div>
        {!isSkip && (
          <div className='stage-field'>
            <Select
              isInModal
              label='Strictness'
              className='stage-strictness'
              options={STRICTNESS_OPTIONS}
              value={strictnessOf(stage)}
              onChange={(option) => {
                const [levelShift, defectShift] = option.value.split(':').map(Number);
                change({ levelShift, defectShift });
              }}
              data-testid={testId('strictness')}
            />
          </div>
        )}
        <div className='stage-actions'>
          <label className='stage-initial'>
            <input
              type='radio'
              name='dynamic-rule-initial-stage'
              checked={!!stage.isInitial}
              onChange={() => change({ isInitial: true })}
              data-testid={testId('isInitial')}
            />
            Starting stage
          </label>
          {canRemove && (
            <span className='link-button delete' onClick={() => onRemove(index)} data-testid={testId('remove')}>
              Remove
            </span>
          )}
        </div>
      </div>

      {isSkip ? (
        <div className='stage-transitions'>
          <div className='transition'>
            <span>Skip for</span>
            <NumberInput {...bind('skipCount')} />
            <span>inspections or</span>
            <NumberInput {...bind('skipMaxPeriod')} />
            <PeriodUnitSelect amount={stage.skipMaxPeriod} {...bind('skipMaxPeriodUnit')} />
            <span>whichever comes first, then go to</span>
            <StageSelect options={otherStages} {...bind('skipNextStageNo')} />
          </div>
        </div>
      ) : (
        <div className='stage-transitions'>
          <div className='transition'>
            <span>After</span>
            <NumberInput {...bind('okCount')} />
            <span>passes in a row, go to</span>
            <StageSelect allowNone options={otherStages} {...bind('okNextStageNo')} />
          </div>
          <div className='transition'>
            <span>After</span>
            <NumberInput {...bind('failCount')} />
            <span>fails in a row, go to</span>
            <StageSelect allowNone options={stageOptions} {...bind('failNextStageNo')} />
          </div>
          <p className='transition-hint'>
            Going to the same stage after a fail starts its pass count again.
          </p>
        </div>
      )}
    </div>
  );
}

export default function SettingsDynamicRuleModal({
  modal,
  onRequestClose,
  onChangeRule,
  onChangeStage,
  onAddStage,
  onRemoveStage,
  onApplyPreset,
  onSave,
}) {
  const { rule, errors, isSaving } = modal;
  const stageOptions = rule.stages
    .filter((stage) => stage.stageNo !== '')
    .map((stage) => ({
      value: Number(stage.stageNo),
      label: `${stage.stageNo} ${stage.name}`.trim(),
    }));

  return (
    <Modal
      title={rule.id ? 'Edit rule' : 'Add rule'}
      className='settings-dynamic-rule-modal'
      isOpen={modal.isOpen}
      ariaHideApp={false}
      onRequestClose={onRequestClose}
      data-testid='SettingsDynamicRuleModal'
    >
      {!rule.id && (
        <Select
          isInModal
          label='Start from an example (optional)'
          className='preset-select'
          options={RULE_PRESETS}
          value={null}
          placeholder='Pick an example'
          onChange={(option) => onApplyPreset(option.value)}
          data-testid='SettingsDynamicRuleModal.preset'
        />
      )}
      <Input
        label='Rule name'
        value={rule.name}
        onChange={(ev) => onChangeRule('name', ev.target.value)}
        errors={errors.name}
        data-testid='SettingsDynamicRuleModal.name'
      />
      <Textarea
        label='Description (optional)'
        className='rule-description'
        value={rule.description}
        onChange={(ev) => onChangeRule('description', ev.target.value)}
        errors={errors.description}
        data-testid='SettingsDynamicRuleModal.description'
      />
      <div className='reset-after'>
        <span>Go back to the starting stage after</span>
        <NumberInput
          value={rule.resetAfterPeriod}
          onChange={(value) => onChangeRule('resetAfterPeriod', value)}
          errors={errors.resetAfterPeriod}
          testId='SettingsDynamicRuleModal.resetAfterPeriod'
        />
        <PeriodUnitSelect
          amount={rule.resetAfterPeriod}
          value={rule.resetAfterPeriodUnit}
          onChange={(value) => onChangeRule('resetAfterPeriodUnit', value)}
          errors={errors.resetAfterPeriodUnit}
          testId='SettingsDynamicRuleModal.resetAfterPeriodUnit'
        />
        <span>without an inspection (leave empty to never reset)</span>
      </div>

      <div className='form-label stages-title'>Stages</div>
      <p className='stages-hint'>
        Each stage either asks the question, at normal, tightened or reduced
        strictness, or skips it. A product and factory pair starts at the
        starting stage.
      </p>
      <div className='dynamic-rule-stages'>
        {rule.stages.map((stage, index) => (
          <StageRow
            key={stage.key}
            stage={stage}
            index={index}
            stageOptions={stageOptions}
            errors={errors}
            onChange={onChangeStage}
            onRemove={onRemoveStage}
            canRemove={rule.stages.length > 1}
          />
        ))}
      </div>
      <span className='link-button' onClick={onAddStage} data-testid='SettingsDynamicRuleModal.addStage'>
        + Add stage
      </span>
      <ErrorBag errors={[...(errors.stages || []), ...(errors.form || [])]} />

      <div className='flex modal-actions'>
        <Button
          type='button'
          className='medium-button'
          onClick={onSave}
          disabled={isSaving}
          data-testid='SettingsDynamicRuleModal.save'
        >
          Save
        </Button>
        <span onClick={onRequestClose} className='link-button'>
          Cancel
        </span>
      </div>
    </Modal>
  );
}
